import { dehydrate, HydrationBoundary } from "@tanstack/react-query";
import { type LoaderFunctionArgs, type MetaFunction } from "react-router";

import { CompilationsList } from "../compilations/components/CompilationsList";
import { useCompilationList } from "../compilations/hooks/useCompilationList";
import { compilationListQuery } from "../compilations/queries";
import { SectionHeader } from "../components/SectionHeader";
import { useDehydratedState } from "../hooks/useDehydratedState";
import { getBrowserQueryClient } from "../providers/queryClient";
import { apiUnreachableInDev, loaderQueryClient } from "../utils/loaders";
import { canonical, pageTitle } from "../utils/meta";

export async function loader({ context }: LoaderFunctionArgs) {
  const queryClient = loaderQueryClient(context);
  await queryClient
    .ensureQueryData(compilationListQuery())
    .catch((error: unknown) => {
      if (!apiUnreachableInDev(error)) throw error;
    });
  return { dehydratedState: dehydrate(queryClient) };
}

// Starts the fetch and returns; the page renders now, its hook fills it in.
export function clientLoader() {
  void getBrowserQueryClient().prefetchQuery(compilationListQuery());
  return { dehydratedState: null };
}

export const meta: MetaFunction = () => [
  { title: pageTitle("Compilations") },
  canonical("/compilations"),
];

export function Compilations() {
  const query = useCompilationList();

  return (
    <main className="container mx-auto px-6 py-12">
      <SectionHeader title="Compilations" className="mb-12" />

      <CompilationsList query={query} variant="grid" />
    </main>
  );
}

export default function CompilationsRoute() {
  const dehydratedState = useDehydratedState();
  return (
    <HydrationBoundary state={dehydratedState}>
      <Compilations />
    </HydrationBoundary>
  );
}
